/**
 * Profile avatar with overlay edit/remove buttons.
 * - Edit button always visible: opens file picker to upload a new image.
 * - Remove button only visible when a custom profile photo is present.
 */
import React, {useRef} from 'react';
import {Avatar} from 'antd';
import {UserOutlined, DeleteOutlined, EditOutlined} from '@ant-design/icons';
import {useViewerStore} from "../../../../store/store";
import azureBLOBStorageService from "../../../../services/azureBLOBStorageService";
import {useCurrentUser} from "../../../../contexts/UserContext";

const ProfileAvatar = () => {
    const {user} = useCurrentUser();
    const profilePhotoInput = useRef<HTMLInputElement>(null);
    const isProfilePhotoAvailable = useViewerStore(state => state.isProfilePhotoAvailable);
    const userProfilePhoto = useViewerStore(state => state.userProfilePhoto);

    // ----- Handlers -----
    const uploadProfilePhotoHandler = (e: any) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = async () => {
                if (reader.result === null) return
                const imageDataUrl = reader.result as string;
                useViewerStore.setState({userProfilePhoto: imageDataUrl});
                useViewerStore.setState({isProfilePhotoAvailable: true});

                const filename = `${user?.user_Id}.png`;
                const fileToUpload = new File([file], filename, {type: file.type});
                const response = await azureBLOBStorageService.uploadFile(fileToUpload);

                if (response !== true) {
                    alert('Failed to upload profile photo');
                } else {
                    if (profilePhotoInput.current) profilePhotoInput.current.value = '';
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const removeProfilePhoto = async () => {
        useViewerStore.setState({isProfilePhotoAvailable: false});
        useViewerStore.setState({userProfilePhoto: undefined});
        const filename = `${user?.user_Id}.png`;
        const response = await azureBLOBStorageService.deleteFile(filename);

        if (response !== true) {
            alert('Failed to delete profile photo');
        }
    };

    const openFilePicker = () => profilePhotoInput.current?.click();

    // ----- Render -----
    return (
        <div className="profile-avatar">
            <Avatar
                shape="square"
                size={88}
                className="profile-avatar__image"
                icon={isProfilePhotoAvailable ? (
                    <img src={userProfilePhoto} alt="profile"/>
                ) : (
                    <UserOutlined/>
                )}
            />
            {/* Edit overlay (top-right) */}
            <button
                type="button"
                className="profile-avatar__btn profile-avatar__btn--edit"
                onClick={openFilePicker}
                aria-label="Edit profile photo"
                title="Edit photo"
            >
                <EditOutlined/>
            </button>
            {/* Remove overlay (bottom-right), only when a custom photo exists */}
            {isProfilePhotoAvailable && (
                <button
                    type="button"
                    className="profile-avatar__btn profile-avatar__btn--remove"
                    onClick={removeProfilePhoto}
                    aria-label="Remove profile photo"
                    title="Remove photo"
                >
                    <DeleteOutlined/>
                </button>
            )}
            <input
                accept={".png, .jpg, .jpeg"}
                onChange={uploadProfilePhotoHandler}
                ref={profilePhotoInput}
                type="file"
                style={{display: 'none'}}
            />
        </div>
    );
};

export default ProfileAvatar;
